import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Stack
} from '@mui/material';
import { Save as SaveIcon, ExpandMore as ExpandMoreIcon, Settings as SettingsIcon, AttachMoney as MoneyIcon, CardGiftcard as GiftIcon, Add as AddIcon, TouchApp as TouchAppIcon } from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLocalization } from '../hooks/useLocalization';

const ClientSettings = () => {
  const { t } = useLocalization();
  const { token } = useAuth();
  const [settings, setSettings] = useState({
    discount_bonus_available: true,
    free_item_bonus_available: true,
    min_user_discount_bonus: 0.0,
    max_user_discount_bonus: 100.0,
    min_free_item_value: 0.0,
    max_free_item_value: 100.0,
    add_attachment_for_each_view: false,
    discount_bonus_probability: 30.0,
    free_item_bonus_probability: 30.0,
    are_attachments_clickable: false,
    attachments_click_points_count: 1,
    max_attachment_click_points: 100,
    enable_pregenerated_attachments: false,
    pregenerated_attachments_for_prompt_count: 5,
    bonus_expiration_days: 30
  });
  const [descriptions, setDescriptions] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axios.get('/client-settings/', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setSettings(response.data);
      setDescriptions(response.data.descriptions || {});
    } catch (err) {
      console.error('Error fetching client settings:', err);
      setError(err.response?.data?.detail || t('failed_to_fetch_client_settings'));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      
      // Prepare settings for saving - convert empty values to appropriate defaults
      const settingsToSave = { ...settings };
      
      // Handle empty number fields by converting to defaults
      const numberFields = [
        'discount_bonus_probability',
        'free_item_bonus_probability', 
        'min_user_discount_bonus',
        'max_user_discount_bonus',
        'min_free_item_value',
        'max_free_item_value'
      ];
      
      const integerFields = [
        'attachments_click_points_count',
        'max_attachment_click_points',
        'pregenerated_attachments_for_prompt_count',
        'bonus_expiration_days'
      ];
      
      // Convert empty number fields to 0
      numberFields.forEach(field => {
        if (settingsToSave[field] === '' || settingsToSave[field] === null || settingsToSave[field] === undefined) {
          settingsToSave[field] = 0;
        }
      });
      
      // Convert empty integer fields to appropriate defaults
      integerFields.forEach(field => {
        if (settingsToSave[field] === '' || settingsToSave[field] === null || settingsToSave[field] === undefined) {
          if (field === 'attachments_click_points_count') {
            settingsToSave[field] = 1;
          } else if (field === 'max_attachment_click_points') {
            settingsToSave[field] = 100;
          } else if (field === 'pregenerated_attachments_for_prompt_count') {
            settingsToSave[field] = 5;
          } else if (field === 'bonus_expiration_days') {
            settingsToSave[field] = 30;
          }
        }
      });
      
      const response = await axios.put('/client-settings/', settingsToSave, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      setSettings(response.data);
      setDescriptions(response.data.descriptions || {});
      setSuccess(t('settings_saved_successfully'));
      
      // Scroll to the top of the page after successful save
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      console.error('Error saving client settings:', err);
      setError(err.response?.data?.detail || t('failed_to_save_client_settings'));
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field, value) => {
    // Allow empty string values for number fields temporarily
    // This allows users to clear the field and enter new values
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleNumberInputChange = (field, value, defaultValue = 0) => {
    // Handle number input changes with proper validation
    let processedValue;
    
    if (value === '' || value === null || value === undefined) {
      // Allow empty values temporarily
      processedValue = '';
    } else {
      // Convert to number and use default if invalid
      const numValue = parseFloat(value);
      processedValue = isNaN(numValue) ? defaultValue : numValue;
    }
    
    setSettings(prev => ({
      ...prev,
      [field]: processedValue
    }));
  };

  const handleIntegerInputChange = (field, value, defaultValue = 1) => {
    const numValue = value === '' ? defaultValue : parseInt(value) || defaultValue;
    setSettings(prev => ({
      ...prev,
      [field]: numValue
    }));
  };

  // Helper function to replace currency placeholders in text
  const replaceCurrencyPlaceholder = (text) => {
    if (!text) return text;
    return text.replace('{currency}', t('currency'));
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography variant="h3" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
          <SettingsIcon sx={{ mr: 2, fontSize: 'inherit' }} />
          {t('client_settings')}
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 300 }}>
          {t('configure_integration_settings_bonus_parameters')}
        </Typography>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}

      {/* Settings Accordion */}
      <Stack spacing={3}>
        {/* Bonus Availability Settings */}
        <Accordion sx={{ boxShadow: 3, borderRadius: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <MoneyIcon sx={{ mr: 2, color: 'primary.main' }} />
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {t('bonus_availability_settings')}
              </Typography>
              <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
                <Chip 
                  label={settings.discount_bonus_available ? t('discount_enabled') : t('discount_disabled')} 
                  color={settings.discount_bonus_available ? "success" : "default"}
                  size="small"
                />
                <Chip 
                  label={settings.free_item_bonus_available ? t('free_items_enabled') : t('free_items_disabled')} 
                  color={settings.free_item_bonus_available ? "success" : "default"}
                  size="small"
                />
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card sx={{ p: 3, bgcolor: 'background.paper' }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.discount_bonus_available}
                        onChange={(e) => handleInputChange('discount_bonus_available', e.target.checked)}
                        color="primary"
                        size="large"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                          {descriptions.discount_bonus_available?.label || t('client_setting_discount_bonus_available_label')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {descriptions.discount_bonus_available?.description || t('client_setting_discount_bonus_available_description')}
                        </Typography>
                      </Box>
                    }
                    sx={{ alignItems: 'flex-start', width: '100%' }}
                  />
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card sx={{ p: 3, bgcolor: 'background.paper' }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.free_item_bonus_available}
                        onChange={(e) => handleInputChange('free_item_bonus_available', e.target.checked)}
                        color="primary"
                        size="large"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                          {descriptions.free_item_bonus_available?.label || t('client_setting_free_item_bonus_available_label')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {descriptions.free_item_bonus_available?.description || t('client_setting_free_item_bonus_available_description')}
                        </Typography>
                      </Box>
                    }
                    sx={{ alignItems: 'flex-start', width: '100%' }}
                  />
                </Card>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Bonus Probability Settings */}
        <Accordion sx={{ boxShadow: 3, borderRadius: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <MoneyIcon sx={{ mr: 2, color: 'info.main' }} />
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {t('bonus_probability_settings')}
              </Typography>
              <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
                <Chip 
                  label={`${t('bonus_type_discount')}: ${settings.discount_bonus_probability}%`}
                  color="info"
                  variant="outlined"
                  size="small"
                />
                <Chip 
                  label={`${t('bonus_type_free_item')}: ${settings.free_item_bonus_probability}%`}
                  color="info"
                  variant="outlined"
                  size="small"
                />
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={descriptions.discount_bonus_probability?.label || t('client_setting_discount_bonus_probability_label')}
                  type="number"
                  value={settings.discount_bonus_probability}
                  onChange={(e) => handleNumberInputChange('discount_bonus_probability', e.target.value)}
                  disabled={!settings.discount_bonus_available}
                  sx={{ 
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: 'background.paper'
                    }
                  }}
                  inputProps={{ step: 0.1, min: 0, max: 100 }}
                  helperText={descriptions.discount_bonus_probability?.description || t('client_setting_discount_bonus_probability_description')}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={descriptions.free_item_bonus_probability?.label || t('client_setting_free_item_bonus_probability_label')}
                  type="number"
                  value={settings.free_item_bonus_probability}
                  onChange={(e) => handleNumberInputChange('free_item_bonus_probability', e.target.value)}
                  disabled={!settings.free_item_bonus_available}
                  sx={{ 
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: 'background.paper'
                    }
                  }}
                  inputProps={{ step: 0.1, min: 0, max: 100 }}
                  helperText={descriptions.free_item_bonus_probability?.description || t('client_setting_free_item_bonus_probability_description')}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Bonus Expiration Settings */}
        <Accordion sx={{ boxShadow: 3, borderRadius: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <MoneyIcon sx={{ mr: 2, color: 'error.main' }} />
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {t('bonus_expiration_settings')}
              </Typography>
              <Box sx={{ ml: 'auto' }}>
                <Chip 
                  label={`${settings.bonus_expiration_days} ${t('days')}`}
                  color="error"
                  variant="outlined"
                />
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={descriptions.bonus_expiration_days?.label || t('client_setting_bonus_expiration_days_label')}
                  type="number"
                  value={settings.bonus_expiration_days}
                  onChange={(e) => handleIntegerInputChange('bonus_expiration_days', e.target.value, 30)}
                  sx={{ 
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: 'background.paper'
                    }
                  }}
                  inputProps={{ step: 1, min: 1, max: 365 }}
                  helperText={descriptions.bonus_expiration_days?.description || t('client_setting_bonus_expiration_days_description')}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Discount Bonus Range */}
        <Accordion sx={{ boxShadow: 3, borderRadius: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <MoneyIcon sx={{ mr: 2, color: 'success.main' }} />
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {t('discount_bonus_range')}
              </Typography>
              <Box sx={{ ml: 'auto' }}>
                <Chip 
                  label={`${settings.min_user_discount_bonus}% - ${settings.max_user_discount_bonus}%`}
                  color="success"
                  variant="outlined"
                />
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={descriptions.min_user_discount_bonus?.label || t('client_setting_min_user_discount_bonus_label')}
                  type="number"
                  value={settings.min_user_discount_bonus}
                  onChange={(e) => handleNumberInputChange('min_user_discount_bonus', e.target.value)}
                  disabled={!settings.discount_bonus_available}
                  sx={{ 
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: 'background.paper'
                    }
                  }}
                  inputProps={{ step: 0.01, min: 0 }}
                  helperText={descriptions.min_user_discount_bonus?.description || t('client_setting_min_user_discount_bonus_description')}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={descriptions.max_user_discount_bonus?.label || t('client_setting_max_user_discount_bonus_label')}
                  type="number"
                  value={settings.max_user_discount_bonus}
                  onChange={(e) => handleNumberInputChange('max_user_discount_bonus', e.target.value)}
                  disabled={!settings.discount_bonus_available}
                  sx={{ 
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: 'background.paper'
                    }
                  }}
                  inputProps={{ step: 0.01, min: 0 }}
                  helperText={descriptions.max_user_discount_bonus?.description || t('client_setting_max_user_discount_bonus_description')}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Free Item Value Range */}
        <Accordion sx={{ boxShadow: 3, borderRadius: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <GiftIcon sx={{ mr: 2, color: 'warning.main' }} />
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {t('free_item_value_range')}
              </Typography>
              <Box sx={{ ml: 'auto' }}>
                <Chip 
                  label={`${settings.min_free_item_value}${t('currency')} - ${settings.max_free_item_value}${t('currency')}`}
                  color="warning"
                  variant="outlined"
                />
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={descriptions.min_free_item_value?.label || t('client_setting_min_free_item_value_label')}
                  type="number"
                  value={settings.min_free_item_value}
                  onChange={(e) => handleNumberInputChange('min_free_item_value', e.target.value)}
                  disabled={!settings.free_item_bonus_available}
                  sx={{ 
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: 'background.paper'
                    }
                  }}
                  inputProps={{ step: 0.01, min: 0 }}
                  helperText={replaceCurrencyPlaceholder(descriptions.min_free_item_value?.description || t('client_setting_min_free_item_value_description'))}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={descriptions.max_free_item_value?.label || t('client_setting_max_free_item_value_label')}
                  type="number"
                  value={settings.max_free_item_value}
                  onChange={(e) => handleNumberInputChange('max_free_item_value', e.target.value)}
                  disabled={!settings.free_item_bonus_available}
                  sx={{ 
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: 'background.paper'
                    }
                  }}
                  inputProps={{ step: 0.01, min: 0 }}
                  helperText={replaceCurrencyPlaceholder(descriptions.max_free_item_value?.description || t('client_setting_max_free_item_value_description'))}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Attachment Settings */}
        <Accordion sx={{ boxShadow: 3, borderRadius: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <AddIcon sx={{ mr: 2, color: 'info.main' }} />
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {t('attachment_settings')}
              </Typography>
              <Box sx={{ ml: 'auto' }}>
                <Chip 
                  label={settings.add_attachment_for_each_view ? t('auto_add_enabled') : t('auto_add_disabled')}
                  color={settings.add_attachment_for_each_view ? "info" : "default"}
                  variant="outlined"
                />
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Card sx={{ p: 3, bgcolor: 'background.paper' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.add_attachment_for_each_view}
                    onChange={(e) => handleInputChange('add_attachment_for_each_view', e.target.checked)}
                    color="primary"
                    size="large"
                  />
                }
                label={
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      {descriptions.add_attachment_for_each_view?.label || t('client_setting_add_attachment_for_each_view_label')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {descriptions.add_attachment_for_each_view?.description || t('client_setting_add_attachment_for_each_view_description')}
                    </Typography>
                  </Box>
                }
                sx={{ alignItems: 'flex-start', width: '100%' }}
              />
            </Card>
          </AccordionDetails>
        </Accordion>

        {/* Clickable Attachments Settings */}
        <Accordion sx={{ boxShadow: 3, borderRadius: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <TouchAppIcon sx={{ mr: 2, color: 'secondary.main' }} />
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {t('clickable_attachments_settings')}
              </Typography>
              <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
                <Chip 
                  label={settings.are_attachments_clickable ? t('clickable_enabled') : t('clickable_disabled')} 
                  color={settings.are_attachments_clickable ? "secondary" : "default"}
                  size="small"
                />
                {settings.are_attachments_clickable && (
                  <Chip 
                    label={`+${settings.attachments_click_points_count} ${t('points_text')} ${t('per_click')}`}
                    color="secondary"
                    variant="outlined"
                    size="small"
                  />
                )}
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Card sx={{ p: 3, bgcolor: 'background.paper' }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.are_attachments_clickable}
                        onChange={(e) => handleInputChange('are_attachments_clickable', e.target.checked)}
                        color="secondary"
                        size="large"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                          {descriptions.are_attachments_clickable?.label || t('client_setting_are_attachments_clickable_label')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {descriptions.are_attachments_clickable?.description || t('client_setting_are_attachments_clickable_description')}
                        </Typography>
                      </Box>
                    }
                    sx={{ alignItems: 'flex-start', width: '100%' }}
                  />
                </Card>
              </Grid>
              {settings.are_attachments_clickable && (
                <>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label={descriptions.attachments_click_points_count?.label || t('client_setting_attachments_click_points_count_label')}
                      type="number"
                      value={settings.attachments_click_points_count}
                      onChange={(e) => handleIntegerInputChange('attachments_click_points_count', e.target.value)}
                      sx={{ 
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          bgcolor: 'background.paper'
                        }
                      }}
                      inputProps={{ step: 1, min: 1 }}
                      helperText={descriptions.attachments_click_points_count?.description || t('client_setting_attachments_click_points_count_description')}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label={descriptions.max_attachment_click_points?.label || t('client_setting_max_attachment_click_points_label')}
                      type="number"
                      value={settings.max_attachment_click_points}
                      onChange={(e) => handleIntegerInputChange('max_attachment_click_points', e.target.value)}
                      sx={{ 
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          bgcolor: 'background.paper'
                        }
                      }}
                      inputProps={{ step: 1, min: 1 }}
                      helperText={descriptions.max_attachment_click_points?.description || t('client_setting_max_attachment_click_points_description')}
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Pre-generated Attachments Settings */}
        <Accordion sx={{ boxShadow: 3, borderRadius: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <AddIcon sx={{ mr: 2, color: 'warning.main' }} />
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {t('pregenerated_attachments_settings')}
              </Typography>
              <Box sx={{ ml: 'auto' }}>
                <Chip 
                  label={settings.enable_pregenerated_attachments ? t('pregeneration_enabled') : t('pregeneration_disabled')}
                  color={settings.enable_pregenerated_attachments ? "warning" : "default"}
                  variant="outlined"
                />
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Card sx={{ p: 3, bgcolor: 'background.paper' }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.enable_pregenerated_attachments}
                        onChange={(e) => handleInputChange('enable_pregenerated_attachments', e.target.checked)}
                        color="warning"
                        size="large"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                          {descriptions.enable_pregenerated_attachments?.label || t('client_setting_enable_pregenerated_attachments_label')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {descriptions.enable_pregenerated_attachments?.description || t('client_setting_enable_pregenerated_attachments_description')}
                        </Typography>
                      </Box>
                    }
                    sx={{ alignItems: 'flex-start', width: '100%' }}
                  />
                </Card>
              </Grid>
              {settings.enable_pregenerated_attachments && (
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={descriptions.pregenerated_attachments_for_prompt_count?.label || t('client_setting_pregenerated_attachments_for_prompt_count_label')}
                    type="number"
                    value={settings.pregenerated_attachments_for_prompt_count}
                    onChange={(e) => handleIntegerInputChange('pregenerated_attachments_for_prompt_count', e.target.value)}
                    sx={{ 
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        bgcolor: 'background.paper'
                      }
                    }}
                    inputProps={{ step: 1, min: 1, max: 20 }}
                    helperText={descriptions.pregenerated_attachments_for_prompt_count?.description || t('client_setting_pregenerated_attachments_for_prompt_count_description')}
                  />
                </Grid>
              )}
            </Grid>
          </AccordionDetails>
        </Accordion>
      </Stack>

      {/* Save Button */}
      <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <Button
          variant="contained"
          size="large"
          startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving}
          sx={{ 
            px: 6, 
            py: 2, 
            borderRadius: 3,
            fontSize: '1.1rem',
            fontWeight: 'bold',
            boxShadow: 3,
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: 6
            },
            transition: 'all 0.3s ease'
          }}
        >
          {saving ? t('saving') : t('save_settings')}
        </Button>
      </Box>
    </Box>
  );
};

export default ClientSettings; 